/*
 * The fleet's settings frame (the reference CMS design @ `c4387239`,
 * `modules-user-management/frontend/components/user-management/SettingsLayout.tsx`).
 *
 * One forced difference: the fleet's menu is a fixed list of four screens —
 * profile, organization, members, API tokens — and this console has only the
 * last of them. The other three are `Link`s to routes that do not exist in our
 * tree, which TanStack Router refuses at compile time, so they are not "left
 * for later" in a way that could still be built: they are absent, and the day a
 * profile screen lands its line comes back from this file's original.
 *
 * `additionalMenuItems` is kept even though nothing passes it yet. It is how the
 * fleet's own app adds a screen the module does not know about, and dropping the
 * prop would mean the next screen is added by editing this file — which is the
 * copy drifting one reasonable edit at a time.
 *
 * The second forced difference is `workspaceId`. The fleet's settings live at
 * `/settings/…` with no workspace in them, because its only answer to "which
 * workspace am I in" is `localStorage`. Ours are addressed
 * (`/workspaces/$workspaceId/settings/…`, design note 0001 §2) — and on this
 * screen that is not a matter of taste: a key grants full access to exactly one
 * workspace, so the address is what says *which* one is being looked at. Two
 * workspaces of your own are the case it decides — issuing a key in one while
 * reading the other, or revoking in the wrong one, is a mistake `localStorage`
 * makes silently and an address cannot.
 *
 * It is deliberately **not** written up as the boundary. Nothing here can
 * surface a workspace this account is not in: `useWorkspace` answers out of
 * `/me`, and the API refuses the rest. Calling the URL shape the protection is
 * the sentence that gets the real check weakened later, so the claim stays the
 * size it is (`sitebrew-console#11`, DevGuru).
 */
import { useTranslation } from "@/hooks/useTranslation";
import { Link } from "@tanstack/react-router";
import { useMemo, type PropsWithChildren } from "react";

export type SettingsMenuItem = {
  id: string;
  label: string;
  to: string;
};

export type SettingsLayoutProps = PropsWithChildren<{
  workspaceId: string;
  additionalMenuItems?: SettingsMenuItem[];
}>;

export const SettingsLayout = ({
  children,
  workspaceId,
  additionalMenuItems = [],
}: SettingsLayoutProps) => {
  const { t } = useTranslation();

  const menuItems = useMemo(
    () => [
      {
        id: "api-tokens",
        label: t("apiTokens.title"),
        to: `/workspaces/${workspaceId}/settings/api-tokens`,
      },
      ...additionalMenuItems,
    ],
    [t, workspaceId, additionalMenuItems]
  );

  return (
    <div className="flex h-full">
      <div className="w-64 border-r bg-muted/40 p-6">
        <div className="space-y-6">
          <h2 className="text-lg font-semibold mb-3">{t("settings.title")}</h2>

          <nav className="flex flex-col space-y-1">
            {menuItems.map((item) => (
              <Link to={item.to} key={item.id}>
                {({ isActive }: { isActive: boolean }) => (
                  <button
                    className={`w-full text-left px-3 py-1 text-sm rounded-md transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : ""
                    }`}
                  >
                    {item.label}
                  </button>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      <div className="flex-1 px-8 py-6">{children}</div>
    </div>
  );
};
